import { SpyneTrait } from 'spyne';
import DeckTextTmpl from 'components/deck/templates/deck-text.tmpl.html';
import DeckAnnotatedTextTmpl from 'components/deck/templates/deck-annotated-text.tmpl.html';
import DeckBulletListTmpl from 'components/deck/templates/deck-bullet-list.tmpl.html';
import DeckCardGridTmpl from 'components/deck/templates/deck-card-grid.tmpl.html';
import DeckTimelineTmpl from 'components/deck/templates/deck-timeline.tmpl.html';
import DeckExhibitTmpl from 'components/deck/templates/deck-exhibit.tmpl.html';
import DeckMarketCirclesTmpl from 'components/deck/templates/deck-market-circles.tmpl.html';
import DeckFlywheelTmpl from 'components/deck/templates/deck-flywheel.tmpl.html';
import DeckFundsBarTmpl from 'components/deck/templates/deck-funds-bar.tmpl.html';
import DeckComparisonTmpl from 'components/deck/templates/deck-comparison.tmpl.html';
import DeckContactLinksTmpl from 'components/deck/templates/deck-contact-links.tmpl.html';
import DeckSectionTmpl from 'components/deck/templates/deck-section.tmpl.html';
import { DeckBlockView } from 'components/deck/deck-block-view.js';

// Block kind → template. Every text-shaped type (headline, eyebrow, lede,
// body, callout, quote, chipRow, statRow, …) shares the text template; only
// kinds whose markup genuinely differs earn an entry.
const templateLookup = {
  annotatedText: DeckAnnotatedTextTmpl,
  bulletList: DeckBulletListTmpl,
  cardGrid: DeckCardGridTmpl,
  timeline: DeckTimelineTmpl,
  exhibit: DeckExhibitTmpl,
  portrait: DeckExhibitTmpl,
  marketCircles: DeckMarketCirclesTmpl,
  flywheel: DeckFlywheelTmpl,
  fundsBar: DeckFundsBarTmpl,
  comparison: DeckComparisonTmpl,
  contactLinks: DeckContactLinksTmpl,
  section: DeckSectionTmpl,
};

// Block kind → element. Anything unlisted is a paragraph.
const tagLookup = {
  titleHeadline: 'h1',
  headline: 'h2',
  callout: 'aside',
  quote: 'blockquote',
  bulletList: 'ul',
  cardGrid: 'div',
  timeline: 'div',
  exhibit: 'figure',
  portrait: 'figure',
  marketCircles: 'figure',
  flywheel: 'figure',
  fundsBar: 'figure',
  comparison: 'section',
  section: 'section',
};

// Bracketed [notes] inside copy are production annotations — never rendered.
const annotationRE = /\s*\[[^\]]*\]\s*/g;
const hasAnnotation = (copy) =>
  typeof copy === 'string' && /\[[^\]]*\]/.test(copy);

// Contact line parts: an email address, or a bare domain with an optional path.
const contactSeparatorRE = /(\s+·\s+)/;
const emailRE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
const domainRE = /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/\S*)?$/i;

// GTM flywheel geometry, in the template's 400×300 drawing space. The ring and
// arrows are art (flywheel-arrows.svg), scaled in decks.scss so the ring's outer
// edge sits at ringOuter around (cx, cy); arcs run clockwise from 9 o'clock, one
// quadrant each. Only the leaders are drawn here, 78 above/below centre, and the
// labels in decks.scss are placed against the same numbers.
const flywheel = {
  cx: 200,
  cy: 150,
  ringOuter: 101.5,
  leaderDy: 78,
  leaderGap: 10,
  dotInset: 6,
};
const flywheelPositions = ['tl', 'tr', 'br', 'bl'];
const roundTenth = (n) => Math.round(n * 10) / 10;

// Build-time record of the art on disk. The weak context bundles no module; it
// only lets a named file that does not exist yet render as a placeholder.
const artContext = import.meta.webpackContext('imgs', {
  recursive: true,
  regExp: /\.(svg|png|jpe?g)$/,
  mode: 'weak',
});
const artPaths = new Map(
  artContext
    .keys()
    .map((key) => [
      key.split('/').pop(),
      `imgs/${key.replace('./', '')}${ASSET_VERSION}`,
    ]),
);

export class DeckBlockTraits extends SpyneTrait {
  constructor(context) {
    let traitPrefix = 'deckBlock$';
    super(context, traitPrefix);
  }

  static deckBlock$GetKind(data = {}, slideLayout) {
    const { type, className, copy } = data;

    if (type === 'headline' && slideLayout === 'title') {
      return 'titleHeadline';
    }
    if (type === 'logo') {
      return 'exhibit';
    }
    if (type === 'exhibit' && className === 'tam-sam-som-circles') {
      return 'marketCircles';
    }
    if (type === 'exhibit' && className === 'exhibit-flywheel') {
      return 'flywheel';
    }
    if (type === 'exhibit' && className === 'exhibit-funds') {
      return 'fundsBar';
    }
    if (
      type === 'contact' &&
      DeckBlockTraits.deckBlock$ContactParts(copy).some((part) => !part.plain)
    ) {
      return 'contactLinks';
    }
    if (type !== 'exhibit' && hasAnnotation(copy) === true) {
      return 'annotatedText';
    }

    return type;
  }

  static deckBlock$GetTemplate(kind) {
    return templateLookup[kind] || DeckTextTmpl;
  }

  static deckBlock$GetTagName(kind) {
    return tagLookup[kind] || 'p';
  }

  static deckBlock$GetClass(kind, data = {}) {
    return [
      'deck-block',
      `deck-block--${data.type}`,
      kind !== data.type ? `deck-block--${kind}` : '',
      data.className || '',
    ]
      .filter((name) => name !== '')
      .join(' ');
  }

  // Adds derived render keys only. Authored keys — copy above all — are never
  // overwritten, so CMS proxy metadata survives on everything authored.
  static deckBlock$PrepareData(data = {}, slideLayout) {
    const kind = DeckBlockTraits.deckBlock$GetKind(data, slideLayout);
    const items = Array.isArray(data.listItems) ? data.listItems : [];

    if (kind === 'annotatedText') {
      data.copyRender = DeckBlockTraits.deckBlock$StripAnnotations(data.copy);
    }
    // A portrait is exhibit art that stays in the main column (only exhibits
    // take the aside), so it shares the exhibit template and art lookup.
    if (kind === 'exhibit' || kind === 'portrait') {
      DeckBlockTraits.deckBlock$AddArt(data);
    }
    if (kind === 'cardGrid') {
      items.forEach((item) => DeckBlockTraits.deckBlock$AddArt(item));
    }
    if (kind === 'bulletList') {
      items.forEach((item) => {
        item.attrBulletType = item.bulletType || 'default';
      });
    }
    if (kind === 'timeline') {
      DeckBlockTraits.deckBlock$SplitEras(data, items);
    }
    if (kind === 'marketCircles') {
      DeckBlockTraits.deckBlock$AddCircles(data);
    }
    if (kind === 'flywheel') {
      DeckBlockTraits.deckBlock$AddArt(data);
      DeckBlockTraits.deckBlock$AddFlywheelArcs(items);
    }
    if (kind === 'fundsBar') {
      DeckBlockTraits.deckBlock$AddFundSegments(data);
    }
    // Competition rows: a title over plain text columns, left to right in
    // data order (current stack, then SpyneJS).
    if (kind === 'comparison') {
      items.forEach((item, i) => {
        item.attrColumn = String(i + 1);
      });
    }
    if (kind === 'contactLinks') {
      data.contactParts = DeckBlockTraits.deckBlock$ContactParts(data.copy);
    }

    return kind;
  }

  // Contact line → parts that read exactly as authored: an email becomes a
  // mailto link, a bare domain an https link, and separators stay text. The
  // parts are derived strings, so the CMS does not pair them; a line with
  // nothing linkable keeps the plain text template and stays editable.
  static deckBlock$ContactParts(copy) {
    return String(copy ?? '')
      .split(contactSeparatorRE)
      .filter((text) => text !== '')
      .map((text) => {
        if (emailRE.test(text) === true) {
          return { mail: { text, attrHref: `mailto:${text}` } };
        }
        if (domainRE.test(text) === true) {
          return { web: { text, attrHref: `https://${text}` } };
        }
        return { plain: { text } };
      });
  }

  // A section is a titled group: its template holds the subhead and a slot,
  // and each authored listItem renders as its own block view inside the slot,
  // so the title and every item stay CMS-editable.
  static deckBlock$onRendered(props = this.props) {
    if (props.dataset?.blockKind !== 'section') {
      return;
    }
    const items = Array.isArray(props.data.listItems) ? props.data.listItems : [];
    const slot = `${props.id$} .deck-section-items`;

    items.forEach((item) => {
      this.appendView(
        new DeckBlockView({ data: item, slideLayout: props.slideLayout }),
        slot,
      );
    });
  }

  static deckBlock$StripAnnotations(copy = '') {
    return String(copy).replace(annotationRE, ' ').trim();
  }

  // A file that exists renders as art; a named file that does not yet exist
  // renders as a placeholder labelled with its filename.
  static deckBlock$AddArt(item = {}) {
    if (typeof item.fileName !== 'string') {
      return;
    }

    const artPath = artPaths.get(item.fileName);

    if (artPath === undefined) {
      item.artPlaceholder = { fileName: item.fileName };
    } else {
      item.artImage = { attrImageSrc: artPath };
    }
  }

  // Traction timeline: bright eras are the dated phases (cards over a pill
  // track), dim eras are the notes after it. Both keep the original items, so
  // their copy stays CMS-paired; only the two derived keys are added.
  static deckBlock$SplitEras(data = {}, items = []) {
    data.phases = items.filter((item) => item?.era === 'bright');
    data.eraNotes = items.filter((item) => item?.era === 'dim');
  }

  // "TAM $112B: … · SAM $31B: … · SOM ~$300M: …" → three nested circles. If the
  // copy ever stops parsing, it renders whole rather than guessing at a split.
  static deckBlock$AddCircles(data = {}) {
    const copy = DeckBlockTraits.deckBlock$StripAnnotations(data.copy);
    const circles = copy.split(' · ').map((part) => {
      const match = part.match(/^(\S+)\s+(\S+):\s+(.+)$/);

      return match === null
        ? null
        : {
            tier: match[1],
            value: match[2],
            note: match[3],
            attrTier: match[1].toLowerCase(),
          };
    });

    if (circles.length >= 1 && circles.every(Boolean) === true) {
      data.circles = circles;
    } else {
      data.circlesFallback = copy;
    }
  }

  // "~70% product & engineering · ~20% … · ~10% …" → one proportional bar.
  // Three shares that sum to 100 draw; anything else renders the copy whole
  // rather than drawing widths the copy doesn't state. Labels take sentence case.
  static deckBlock$AddFundSegments(data = {}) {
    const copy = DeckBlockTraits.deckBlock$StripAnnotations(data.copy);
    const segments = copy.split(' · ').map((part, i) => {
      const match = part.match(/^(~?(\d+)%)\s+(.+)$/);

      return match === null
        ? null
        : {
            share: match[1],
            label: match[3].charAt(0).toUpperCase() + match[3].slice(1),
            attrShare: match[2],
            attrTier: String(i + 1),
          };
    });
    const total = segments.reduce(
      (sum, segment) => sum + (segment === null ? NaN : Number(segment.attrShare)),
      0,
    );

    if (segments.length === 3 && total === 100) {
      data.fundSegments = segments;
    } else {
      data.fundsFallback = copy;
    }
  }

  // Flywheel: each arc item gets its quadrant and the leader out to its label.
  // Derived keys only; copy and attrStatus (live | pending) stay as authored.
  static deckBlock$AddFlywheelArcs(items = []) {
    const { cx, cy, ringOuter, leaderDy, leaderGap, dotInset } = flywheel;
    const reach =
      Math.sqrt(ringOuter * ringOuter - leaderDy * leaderDy) + leaderGap;

    items.slice(0, flywheelPositions.length).forEach((item, i) => {
      const position = flywheelPositions[i];
      const side = position.endsWith('l') ? -1 : 1;
      const leaderY = cy + (position.startsWith('t') ? -leaderDy : leaderDy);
      const dotX = side === -1 ? dotInset : cx * 2 - dotInset;

      item.attrPosition = position;
      item.attrLeaderPath = `M${dotX} ${leaderY}H${roundTenth(cx + side * reach)}`;
      item.attrDotX = String(dotX);
      item.attrDotY = String(leaderY);
    });
  }
}
